import React, { FC, Fragment } from 'react';
import { Box, Grid, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { SubcontractSelectionRecord } from 'models/order';
import { formatDate } from 'utils/format';

type SubcontractSelectionProps = {
    selection: SubcontractSelectionRecord;
};

/**
 * DD-3485 Marketplace: what this sub-order was actually sent with, as recorded when it
 * was created — independent of the partner defaults today. The backend resolves the
 * record only for the lab that subcontracted the order, so this never renders for the
 * subcontractor.
 */
const SubcontractSelection: FC<SubcontractSelectionProps> = ({ selection }) => {
    const { t: translate } = useTranslation(['orders', 'common']);
    const yesOrNo = (value: boolean) => translate(value ? 'common:YES' : 'common:NO');

    const rows: { label: string; value: string }[] = [
        { label: translate('orders:CONSTRUCTIONS'), value: String(selection.restorationCount) },
        { label: translate('orders:SUBCONTRACT_DEFAULT_ATTACHMENTS'), value: String(selection.attachmentCount) },
        { label: translate('orders:SUBCONTRACT_DEFAULT_SCAN_CASE_FILES'), value: String(selection.scanCaseFileCount) },
        { label: translate('orders:PATIENT'), value: yesOrNo(selection.patientReference) },
        {
            label: translate('orders:SUBCONTRACT_PATIENT_IDENTITY_NUMBER'),
            value: yesOrNo(selection.patientIdentityNumber),
        },
        { label: translate('orders:DENTIST'), value: yesOrNo(selection.dentistName) },
        { label: translate('orders:SUBCONTRACT_MESSAGE_TO_SUBCONTRACTOR'), value: yesOrNo(selection.comment) },
    ];

    return (
        <Box data-test="subcontract-selection">
            <Typography variant="body2" color="text.secondary" mb={1}>
                {translate('orders:SUBCONTRACT_SELECTION_INFO')}
                {selection.confirmedAt ? ` ${formatDate(new Date(selection.confirmedAt), 'datetime')}` : ''}
            </Typography>
            <Grid container spacing={0.5}>
                {rows.map((row) => (
                    <Fragment key={row.label}>
                        <Grid item xs={7}>
                            <Typography variant="subtitle2">{row.label}</Typography>
                        </Grid>
                        <Grid item xs={5}>
                            <Typography variant="body2">{row.value}</Typography>
                        </Grid>
                    </Fragment>
                ))}
            </Grid>
        </Box>
    );
};

export default SubcontractSelection;
